import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import ClientPageLayout from '../../components/shared/layouts/ClientPageLayout';
import AnnouncementCard from '../../components/shared/AnnouncementCard';
import { useAnnouncements } from '../../hooks/useAnnouncements';
import { useClientPendingInvoices } from '../../services/clientInvoiceService';
import { 
  User, 
  Mail, 
  Phone, 
  Key, 
  Shield, 
  CheckCircle, 
  Edit2,
  Copy,
  Eye,
  EyeOff,
  FileText,
  ArrowRight,
  Calendar,
} from 'lucide-react';

const ClientDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState(false);

  // Fetch announcements
  const { data: announcementsData } = useAnnouncements();
  const announcements = announcementsData?.data || [];

  const { data: pendingInvoicesRes } = useClientPendingInvoices();
  const pendingInvoiceCount = pendingInvoicesRes?.data?.count ?? 0;

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const accountInfo = [
    {
      label: 'Username',
      value: user?.name || 'Client',
      icon: User,
      copyable: false
    },
    {
      label: 'Email',
      value: user?.email || 'N/A',
      icon: Mail,
      copyable: true
    },
    {
      label: 'Phone number',
      value: user?.phone || 'N/A',
      icon: Phone,
      copyable: true
    },
    {
      label: 'User ID',
      value: user?.id?.substring(0, 8) || 'N/A',
      icon: Shield,
      copyable: true,
      isMono: true
    }
  ];

  return (
    <ClientPageLayout
      title="My Account"
      subtitle="Review your profile, contact details, and account security at a glance."
      guideKey="account"
    >
      <div className="space-y-5">
        {/* Announcements */}
        {announcements.length > 0 && (
          <div>
            {announcements.map((announcement) => (
              <AnnouncementCard
                key={announcement.id}
                announcement={announcement}
              />
            ))}
          </div>
        )}

        {pendingInvoiceCount > 0 && (
          <div className="rounded-2xl border border-amber-200/80 bg-amber-50 p-4 sm:p-5 dark:border-amber-800/50 dark:bg-amber-950/30">
            <div className="flex flex-col sm:flex-row sm:items-start gap-4">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <FileText className="w-5 h-5 text-amber-800 dark:text-amber-300" />
                </div>
                <div>
                  <h3 className="text-sm sm:text-base font-bold text-amber-950 dark:text-amber-100">
                    Pending invoice{pendingInvoiceCount > 1 ? 's' : ''}
                  </h3>
                  <p className="text-sm text-amber-900/90 dark:text-amber-200/90 mt-1">
                    You have {pendingInvoiceCount} invoice
                    {pendingInvoiceCount > 1 ? 's' : ''} awaiting your attention from a vendor.
                    Open My Bookings to view the linked booking and download each invoice.
                  </p>
                </div>
              </div>
              <Link
                to="/bookings"
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-amber-900 text-white text-sm font-medium !rounded-xl hover:bg-amber-950 transition-colors whitespace-nowrap dark:bg-amber-700 dark:hover:bg-amber-600"
              >
                View bookings
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        )}

        {/* Profile Header Card */}
        <div className="rounded-2xl border border-gray-200/80 bg-white p-6 sm:p-8 shadow-sm dark:border-gray-700 dark:bg-gray-900">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
            {/* Avatar */}
            <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gray-900 dark:bg-gray-100 rounded-2xl flex items-center justify-center flex-shrink-0">
              <span className="text-white dark:text-gray-900 font-bold text-2xl sm:text-3xl">
                {user?.name ? user.name.charAt(0).toUpperCase() : 'C'}
              </span>
            </div>
            
            {/* Profile Info */}
            <div className="flex-1 min-w-0">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-50 mb-2">
                {user?.name || 'Client'}
              </h2>
              <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mb-3">
                {user?.email || 'No email provided'}
              </p>
              <div className="flex items-center space-x-2">
                {user?.is_active ? (
                  <span className="inline-flex items-center px-3 py-1 bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300 rounded-full text-xs sm:text-sm font-medium">
                    <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 mr-1.5" />
                    Active Account
                  </span>
                ) : (
                  <span className="inline-flex items-center px-3 py-1 bg-gray-200 text-gray-700 dark:bg-gray-800 dark:text-gray-300 rounded-full text-xs sm:text-sm font-medium">
                    Inactive Account
                  </span>
                )}
              </div>
            </div>

            {/* Edit Button */}
            <button 
              onClick={() => navigate('/settings')}
              className="flex items-center space-x-2 px-4 py-2 border border-gray-200/80 dark:border-gray-700 !rounded-xl text-gray-900 dark:text-gray-50 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors active:scale-95"
            >
              <Edit2 className="w-4 h-4" />
              <span className="text-sm font-medium">Edit Profile</span>
            </button>
          </div>
        </div>

        {/* Account Information Card */}
        <div className="rounded-2xl border border-gray-200/80 bg-white p-4 sm:p-6 shadow-sm dark:border-gray-700 dark:bg-gray-900">
          <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-50 mb-4 sm:mb-6">Account Information</h3>
          <div className="space-y-4">
            {accountInfo.map((info, index) => {
              const Icon = info.icon;
              return (
                <div
                  key={index}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between py-3 sm:py-4 border-b border-gray-200 dark:border-gray-700 last:border-b-0 gap-2 sm:gap-0"
                >
                  <div className="flex items-center space-x-3 flex-1 min-w-0">
                    <div className="w-10 h-10 bg-gray-100 dark:bg-gray-800 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-gray-900 dark:text-gray-100" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 font-medium mb-1">{info.label}</p>
                      <p className={`text-sm sm:text-base text-gray-900 dark:text-gray-50 font-semibold truncate ${info.isMono ? 'font-mono' : ''}`}>
                        {info.value}
                      </p>
                    </div>
                  </div>
                  {info.copyable && info.value !== 'N/A' && (
                    <button
                      onClick={() => copyToClipboard(info.value)}
                      className="flex items-center space-x-1 px-3 py-1.5 text-xs sm:text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-50 border border-gray-200 dark:border-gray-700 !rounded-xl hover:border-gray-300 dark:hover:border-gray-600 transition-colors"
                      title="Copy to clipboard"
                    >
                      <Copy className="w-3 h-3 sm:w-4 sm:h-4" />
                      <span>{copied ? 'Copied!' : 'Copy'}</span>
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Security Card */}
        <div className="rounded-2xl border border-gray-200/80 bg-white p-4 sm:p-6 shadow-sm dark:border-gray-700 dark:bg-gray-900">
          <div className="flex items-center space-x-3 mb-4 sm:mb-6">
            <div className="w-10 h-10 bg-gray-100 dark:bg-gray-800 rounded-xl flex items-center justify-center">
              <Key className="w-5 h-5 text-gray-900 dark:text-gray-100" />
            </div>
            <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-50">Security</h3>
          </div>
          
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between py-3 sm:py-4 gap-3 sm:gap-0">
            <div className="flex-1">
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 font-medium mb-1">Password</p>
              <div className="flex items-center space-x-2">
                <span className="text-sm sm:text-base text-gray-900 dark:text-gray-50 font-mono">
                  {showPassword ? '••••••••' : '••••••••'}
                </span>
                <button
                  onClick={() => setShowPassword(!showPassword)}
                  className="p-1 text-gray-400 hover:text-gray-900 dark:hover:text-gray-50 transition-colors"
                  title={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>
            <button 
              onClick={() => navigate('/settings')}
              className="flex items-center space-x-2 px-4 py-2 border border-gray-200/80 dark:border-gray-700 !rounded-xl text-gray-900 dark:text-gray-50 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors active:scale-95"
            >
              <Key className="w-4 h-4" />
              <span className="text-sm font-medium">Change Password</span>
            </button>
          </div>
        </div>

        {/* Quick Actions Card */}
        <div className="rounded-2xl border border-gray-200/80 bg-white p-4 sm:p-6 shadow-sm dark:border-gray-700 dark:bg-gray-900">
          <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-50 mb-4 sm:mb-6">Quick Actions</h3>
          <div className="grid grid-cols-1 gap-3 sm:gap-4">
            <Link
              to="/bookings"
              className="group flex items-center gap-3 p-4 rounded-2xl border border-gray-200/80 dark:border-gray-700 hover:border-sky-300 dark:hover:border-sky-700 hover:bg-sky-50/50 dark:hover:bg-sky-950/20 transition-colors"
            >
              <div className="w-11 h-11 bg-sky-50 dark:bg-sky-950/40 border border-sky-100 dark:border-sky-800 rounded-xl flex items-center justify-center flex-shrink-0">
                <Calendar className="w-5 h-5 text-sky-700 dark:text-sky-300" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-50">My Bookings</p>
                <p className="text-xs text-gray-600 dark:text-gray-400">View events and invoices</p>
              </div>
              <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-sky-600 dark:group-hover:text-sky-400 flex-shrink-0" />
            </Link>
          </div>
        </div>
      </div>
    </ClientPageLayout>
  );
};

export default ClientDashboard;
